<template>
  <div>
    <div v-if="show" class="modal-backdrop" @click="$emit('close')"></div>
    <div v-if="show" class="modal d-block" id="financialModal" tabindex="-1" role="dialog" aria-labelledby="financialModalLabel"
         aria-hidden="true">
      <div class="modal-dialog" role="document" style="margin: 0;">
        <div class="modal-content p-0" style="border-radius: 18px; max-width: 480px; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); height: 70vh; max-height: 70vh; overflow-y: auto; border: 2px solid #8a4baf; box-shadow: 0 4px 24px rgba(80, 0, 120, 0.10);">
          <div class="snapshot-card">
            <div class="card-border">
              <div class="top-section">
                <span class="ai-badge">
                  <!-- Sparkle icon as shown in the design -->
                  <svg class="ai-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                  AI Overview
                </span>

                <button type="button" class="close-btn" @click="$emit('close')" aria-label="Close">
                  &times;
                </button>
              </div>

              <div v-if="loading" class="loading-container">
                <div class="loading-spinner"></div>
                <p>Loading financial data...</p>
              </div>

              <template v-else>
                <div class="header-section">
                  <h1>{{ financialData.heading }}</h1>
                  <p class="subtitle">({{ financialData.form_type }}, filed on {{ financialData.file_date }})</p>
                </div>

                <div class="data-row" v-for="(value, key) in formattedData" :key="key">
                  <div class="data-label">{{ key }}</div>
                  <div class="data-value">{{ value }}</div>
                </div>

                <div class="footer">
                  Sourced from LifeMD's {{ financialData.form_type }} • Filed: {{ financialData.file_date }}<br>
<!--                <a href="#" class="view-link">View Full Filing</a>-->
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FinancialSnapshotModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    secId: String,
    accountId: String,
  },
  data() {
    return {
      loading: false,
      financialData: {},
    }
  },
  computed: {
    formattedData() {
      const formatted = {};
      for (const [key, value] of Object.entries(this.financialData)) {
        // Skip form_type and filing_date as they are handled separately
        if (key === 'form_type' || key === 'file_date' || key === 'heading') continue;

        // Convert key to human readable format
        const formattedKey = key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        formatted[formattedKey] = value;
      }
      return formatted;
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.secId && this.accountId) {
          this.fetchFinancialData();
        }
      }
    }
  },
  methods: {
    async fetchFinancialData() {
      this.loading = true;
      try {
        const response = await fetch(
            `${process.env.VUE_APP_API_BASE_URL}/sec/analysis/v2?accountId=${this.accountId}&secId=${this.secId}`
        );
        const data = await response.json();
        this.financialData = data;
      } catch (error) {
        console.error('Error fetching financial data:', error);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style>
.snapshot-card {
  width: 100%;
  border-radius: 12px;
  border: none;
  overflow: hidden;
  background-color: white;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.snapshot-card::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.card-border {
  border: 2px solid #8a4baf;
  border-radius: 12px;
  padding: 20px 25px;
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.top-section {
  background-color: #f8f9fa;
  padding: 15px 20px;
  margin: -20px -25px 20px -25px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ai-badge {
  background-color: #f5f0fa;
  color: #333;
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  font-weight: 500;
}

.ai-icon {
  color: #8a4baf;
  margin-right: 6px;
  font-size: 16px;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 25px;
  font-size: 20px;
  color: #333;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1;
}

.header-section {
  background-color: #f8f9fa;
  padding: 15px 20px;
  margin: -20px -25px 20px -25px;
  border-bottom: 1px solid #e9ecef;
}

h1 {
  font-size: 28px;
  font-weight: 700;
  margin-top: 15px;
  margin-bottom: 5px;
  color: #111;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.subtitle {
  font-size: 16px;
  color: #555;
  margin-bottom: 25px;
}

.transaction-heading {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.4;
}

.data-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #a8e0d9;
  gap: 16px;
}

.data-row:last-child {
  border-bottom: none;
}

.data-label {
  font-weight: 600;
  color: #222;
  font-size: 16px;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 50%;
}

.data-value {
  text-align: right;
  color: #333;
  font-size: 16px;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 50%;
}

.footer {
  margin-top: 15px;
  font-size: 14px;
  color: #555;
}

.view-link {
  color: #5bc2c0;
  text-decoration: none;
  font-weight: 500;
}

.view-link:hover {
  text-decoration: underline;
  color: #4aa3a1;
}

.multi-line {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* Modal specific styles */
.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

.modal-dialog {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: transparent !important;
  z-index: 9999;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  min-height: 90vh;
  width: 100%;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #8a4baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
